import { useCallback, useEffect, useSyncExternalStore } from 'react';

const getLocalStorageItem = (key) => {
  return window.localStorage.getItem(key);
};

function dispatchStorageEvent(key, newValue) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}

const removeLocalStorageItem = (key) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const setLocalStorageItem = (key, value) => {
  const stringifiedValue = JSON.stringify(value);

  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const useLocalStorageSubscribe = (callback) => {
  window.addEventListener('storage', callback);

  return () => window.removeEventListener('storage', callback);
};

const getLocalStorageServerSnapshot = () => {
  // throw Error('useLocalStorage is a client-only hook');
  return {};
};

export function useLocalStorage(
  key: string,
  initialValue: Record<string, unknown>,
) {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = useCallback(
    (v) => {
      try {
        const nextState = typeof v === 'function' ? v(JSON.parse(store)) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store],
  );

  useEffect(() => {
    if (
      getLocalStorageItem(key) === null &&
      typeof initialValue !== 'undefined'
    ) {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  return [store ? JSON.parse(store) : initialValue, setState];
}
